/**
 * 67.67: a vehicle's drivetrain, as its voice hears it (the owner, Oct 19: "for vehicles I think a simulated engine noise
 * with simulated transition / gear number so when climbi[ng]…"; on the brief: "Good assumptions on drivetrain yep that's
 * what I was thinking"). The sim moves the body (core/drive.ts: its speed, its throttle, the hill under it, its turn);
 * this follows it with an engine and a gearbox, so the voice has revs, a gear, a load and the shifts between them:
 *
 * - **revs** from the speed through the gear's ratio (`tops`: each gear's share of the body's top speed at the redline;
 *   the top gear a cruising one, the top speed at about four-fifths of the redline). At a stop it idles; pulling away
 *   the clutch (or the converter) slips, the revs above the wheels'.
 * - **the gear** by a shift schedule the load moves: up at `upAt(load)` (light: early; flat out: at the redline), down
 *   when the gear below would still sit under that less a margin, and a least time in a gear — so it never hunts.
 *   Flat out (`KICKDOWN`) it drops to any gear whose revs fit under the redline.
 *   Running downhill on the wheels it holds its gear (engine braking) and steps down as it slows.
 * - **a shift** is a transition, not a step: for `shiftS` the load lifts (a clutch to nothing, a converter to a share)
 *   and the revs fall toward (or are blipped up to) the next gear's; then the load comes back.
 * - **the load** (−1 … 1) from the throttle, how hard it is gathering speed and the grade under the hull: up a hill it
 *   works (it kicks down, high revs, loud and rough); down one with the throttle shut the wheels drive the engine
 *   (negative: engine braking, or an electric motor's regen).
 * - **turning on the spot**: a tracked body's tracks counter-rotate — it revs and works standing.
 * - **electric** (the owner: "Electric 1 or 2 gears, not actually sure how that works in reality"): one fixed reduction,
 *   the motor's speed the wheels' — no idle, no shifts; `ELECTRIC_TWO_SPEED` the two-gear case, a dial.
 *
 * Pure numbers: no audio, nothing in the sim read but what the voice is handed, nothing written. `stepDrivetrain` takes
 * last frame's state and returns this frame's (vehicleSound.ts keeps one per voiced vehicle); `settledDrivetrain` is
 * where a steady drive would sit (a voice's first frame, and the stateless `hearVehicle`).
 */
import { DRIVE_DIALS } from '../constants';
import type { VehicleVoiceKind } from './vehicleMix';

/** What the drivetrain reads each frame — the sim's, as the voice reads it (vehicleMix `vehicleStateOf`). */
export interface DriveInput {
  running: boolean;
  /** the speed's share of the body's top speed (≥ 0; over 1 downhill) */
  speedFrac: number;
  /** the sim's throttle, −1 … 1 (rolling forward, −1 brakes; from a stop it backs up) */
  throttle: number;
  /** the ground's rise over run along the heading (+ up) */
  grade: number;
  /** turning: the yaw rate's share of the body's turn on the spot (0 for a body that cannot) */
  turn: number;
  /** backing up */
  reverse: boolean;
  /** out of gear, standing (the sheet's rev blip; nothing in play puts it there): the revs follow the throttle freely */
  neutral?: boolean;
}

/**
 * A body's gearbox and engine: `tops` (each forward gear's share of top speed at the redline), `reverseTop`, a torque
 * `converter` (automatic: slips, softer shifts) or a clutch, `shiftS` a shift's length (s), `launch` the revs pulling away
 * (0 idle … 1 redline), `spinUp`/`spinDown` the engine's time constants (s), `hold` the least time in a gear (s), `rpm`
 * idle and redline for the readout, `pivot` the revs turning on the spot. Few, wide gears and quick shifts: the sim's
 * bodies reach their top in 3–4 s (DRIVE_DIALS' accel), so a real truck's six or more would change every half second —
 * the speed runs on through a shift (the sim knows no gears), and a long one would land past the next gear's redline.
 */
export interface GearboxSpec {
  tops: number[]; reverseTop: number; converter: boolean; shiftS: number; launch: number;
  spinUp: number; spinDown: number; hold: number; rpm: readonly [number, number]; pivot: number;
}
export const GEARBOXES: Record<VehicleVoiceKind, GearboxSpec> = {
  bikeGas: { tops: [0.3, 0.5, 0.72, 1.0, 1.28], reverseTop: 0.3, converter: false, shiftS: 0.12, launch: 0.38, spinUp: 0.05, spinDown: 0.1, hold: 0.35, rpm: [1400, 10500], pivot: 0 },
  bikeElectric: { tops: [1.1], reverseTop: 1.1, converter: false, shiftS: 0, launch: 0, spinUp: 0.08, spinDown: 0.08, hold: 0, rpm: [0, 9000], pivot: 0 },
  technical: { tops: [0.32, 0.6, 1.0, 1.28], reverseTop: 0.3, converter: false, shiftS: 0.24, launch: 0.3, spinUp: 0.1, spinDown: 0.16, hold: 0.5, rpm: [800, 5200], pivot: 0 },
  truck: { tops: [0.3, 0.58, 1.0, 1.28], reverseTop: 0.28, converter: false, shiftS: 0.28, launch: 0.28, spinUp: 0.12, spinDown: 0.2, hold: 0.55, rpm: [700, 4400], pivot: 0 },
  transport: { tops: [0.3, 0.6, 1.0, 1.3], reverseTop: 0.28, converter: false, shiftS: 0.3, launch: 0.26, spinUp: 0.18, spinDown: 0.26, hold: 0.6, rpm: [650, 2600], pivot: 0 },
  // the M113 (A1 on): the Detroit 6V53 two-stroke through an Allison automatic — a converter, quick soft shifts
  tracked: { tops: [0.3, 0.6, 1.04, 1.3], reverseTop: 0.3, converter: true, shiftS: 0.3, launch: 0.42, spinUp: 0.2, spinDown: 0.32, hold: 0.8, rpm: [650, 2800], pivot: 0.55 },
};
/** The electric bike's two-gear case (a dial, the sheet's: a Taycan has two, most EVs one). */
export const ELECTRIC_TWO_SPEED: GearboxSpec = { ...GEARBOXES.bikeElectric, tops: [0.62, 1.1], shiftS: 0.18, hold: 0.8 };

/** How fast each body gathers speed flat out on the flat: the speed's share of its top a second (DRIVE_DIALS' accel / top). */
const BODY_OF: Record<VehicleVoiceKind, keyof typeof DRIVE_DIALS> = { bikeGas: 'motorcycle', bikeElectric: 'motorcycle', technical: 'technical', truck: 'supplyTruck', transport: 'wheeled', tracked: 'tracked' };
export function gatherRate(kind: VehicleVoiceKind): number {
  const b = DRIVE_DIALS[BODY_OF[kind]][kind === 'bikeElectric' ? 'electric' : 'gas'];
  return b.accel / b.top;
}

/** A drivetrain this frame: its gear (1 … n, −1 reverse), revs (0 idle … 1 redline), load (−1 … 1), a shift under way. */
export interface DrivetrainState {
  gear: number;
  rev: number;
  load: number;
  shift: { from: number; to: number; t: number } | null;
  /** seconds since the last change */
  inGear: number;
  /** last frame's speed share (− backing up) and how fast it is gathering (smoothed, share a second) */
  speed: number;
  gather: number;
}

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
const ease = (from: number, to: number, dt: number, tau: number) => (tau <= 0 ? to : from + (to - from) * (1 - Math.exp(-dt / tau)));

/** Where the up-shift sits at a load: light, early; flat out, the redline. Running on the wheels downhill it holds the gear. */
export function upAt(load: number, downhill = false): number { return downhill ? 0.97 : 0.5 + 0.5 * clamp(load, 0, 1); }
/** The down-shift's margin under `upAt`: the gear below must sit this far under the up point (the hysteresis). */
export const SHIFT_MARGIN = 0.06;
/** Flat out (the load at least this): it kicks down to any gear whose revs fit under the redline. */
export const KICKDOWN = 0.95;

/** The work asked of it (−1 … 1): the throttle, how hard it gathers speed (0 … 1 of its flat-out rate), the climb, the turn. */
export function demandOf(d: DriveInput, gathering: number): number {
  if (!d.running) return 0;
  const speed = Math.abs(d.speedFrac), effort = d.reverse ? Math.max(0, -d.throttle) : Math.max(0, d.throttle);
  const braking = speed > 0.03 && (d.reverse ? d.throttle > 0 : d.throttle < 0);
  const climb = d.grade * (d.reverse ? -1 : 1), turn = clamp(d.turn, 0, 1);
  if (speed < 0.08 && turn > 0.1) return clamp(0.75 * turn + 0.25 * effort, 0, 1);   // on the spot: the tracks counter-rotate
  if (effort > 0.02) return clamp(effort * (0.5 + 0.4 * clamp(gathering, 0, 1)) + 2.5 * climb + 0.25 * turn, 0.05, 1);
  if (speed < 0.03) return 0.05;   // idling
  // the throttle shut, rolling: the wheels drive the engine — harder downhill, braking
  return -clamp(0.15 + 3 * Math.max(0, -climb) + (braking ? 0.3 : 0), 0, 1);
}

/**
 * The revs it goes to in `gear` at this speed share: the wheels' (`wheels`: the clutch in, the engine turned by them, a
 * converter's slip over them), else free — idling, the clutch's launch in first, turning on the spot.
 */
function revTarget(g: GearboxSpec, gear: number, d: DriveInput, load: number): { rev: number; wheels: boolean } {
  const speed = Math.abs(d.speedFrac), effort = d.reverse ? Math.max(0, -d.throttle) : Math.max(0, d.throttle);
  if (!d.running) return { rev: 0, wheels: false };
  if (d.neutral) return { rev: 0.95 * effort, wheels: false };
  if (speed < 0.08 && d.turn > 0.1) return { rev: Math.max(speed / g.tops[0], g.pivot * clamp(d.turn, 0, 1) + 0.2 * effort), wheels: false };
  if (speed < 0.03 && effort < 0.02) return { rev: 0, wheels: false };   // idle
  const top = gear < 0 ? g.reverseTop : g.tops[clamp(gear, 1, g.tops.length) - 1];
  const locked = speed / top;
  const slip = g.converter ? 0.12 * Math.max(0, load) * (1 - Math.min(1, locked)) : 0;
  const launch = (gear === 1 || gear < 0) && effort > 0 ? g.launch * effort : 0;
  return { rev: Math.min(1.06, Math.max(locked + slip, launch)), wheels: locked + slip >= launch };
}
/** The clutch in, the revs follow the wheels this closely (s): a gear is a gear. */
const LOCKED_TAU = 0.03;

/** The gear a steady drive at this speed and load sits in (the lowest whose revs are under the up point). */
function gearFor(g: GearboxSpec, d: DriveInput, load: number): number {
  if (d.reverse && Math.abs(d.speedFrac) > 0.01) return -1;
  const speed = Math.abs(d.speedFrac), up = upAt(load, load < -0.2);
  let gear = 1;
  while (gear < g.tops.length && speed / g.tops[gear - 1] >= up) gear++;
  return gear;
}

/** Where a steady drive sits: a voice's first frame (and the stateless reading, `hearVehicle`). */
export function settledDrivetrain(g: GearboxSpec, d: DriveInput): DrivetrainState {
  const load = demandOf(d, 0), gear = gearFor(g, d, load);
  const signed = d.reverse ? -Math.abs(d.speedFrac) : Math.abs(d.speedFrac);
  return { gear, rev: revTarget(g, gear, d, load).rev, load, shift: null, inGear: 10, speed: signed, gather: 0 };
}

/** One frame (`dt` seconds) of the drivetrain under this frame's drive. */
export function stepDrivetrain(st: DrivetrainState, g: GearboxSpec, d: DriveInput, dt: number, gatherMax: number): DrivetrainState {
  if (!(dt > 0)) return st;
  dt = Math.min(dt, 0.1);
  const speed = Math.abs(d.speedFrac), signed = d.reverse ? -speed : speed;
  const gather = ease(st.gather, (signed - st.speed) / dt * (d.reverse ? -1 : 1), dt, 0.3);
  const want = demandOf(d, gatherMax > 0 ? gather / gatherMax : 0);
  const downhill = want < -0.2;
  let { gear, shift, inGear } = st;
  inGear += dt;
  // the gear: reverse and a stop at once; forward by the schedule, a shift taking `shiftS`
  if (!d.running) { gear = 1; shift = null; }
  else if (d.reverse && speed > 0.01) { gear = -1; shift = null; }
  else if (gear < 0 || speed < 0.03) { if (gear !== 1) inGear = 0; gear = 1; shift = null; }
  else if (shift) {
    shift = { ...shift, t: shift.t + dt };
    if (shift.t >= g.shiftS) { gear = shift.to; shift = null; inGear = 0; }
  } else {
    const n = g.tops.length, up = upAt(want, downhill), rev = speed / g.tops[gear - 1];
    let to = gear;
    if (gear < n && (rev >= 1.02 || (rev >= up && inGear >= g.hold))) to = gear + 1;   // the limiter shifts at once
    else if (gear > 1 && inGear >= g.hold) {
      // down when the gear below sits under the up point less the margin; flat out (a kickdown) whenever it fits under the redline
      const lower = speed / g.tops[gear - 2];
      if (lower <= up - SHIFT_MARGIN || (want >= KICKDOWN && lower <= 0.97)) to = gear - 1;
    }
    if (to !== gear) {
      if (g.shiftS > 0) shift = { from: gear, to, t: 0 };
      else { gear = to; inGear = 0; }
    }
  }
  // the load and the revs: through a shift the load lifts (a clutch's to nothing, a converter's to a share) and the revs
  // go to the next gear's — falling on the way up, blipped on the way down
  const loadTo = shift ? (g.converter ? 0.4 * Math.max(0, want) : Math.min(0, want) * 0.3) : want;
  const to = revTarget(g, shift ? shift.to : gear, d, want);
  const rev = ease(st.rev, to.rev, dt, to.wheels && !shift ? LOCKED_TAU : to.rev > st.rev ? g.spinUp : g.spinDown);
  const load = ease(st.load, loadTo, dt, 0.08);
  return { gear, rev, load, shift, inGear, speed: signed, gather };
}

/** The readout: the gear ("R", "1", "2→3"), the rpm, the load in words. */
export function drivetrainReadout(st: DrivetrainState, g: GearboxSpec): { gear: string; rpm: number; load: string } {
  const gear = st.shift ? `${st.shift.from}→${st.shift.to}` : st.gear < 0 ? 'R' : String(st.gear);
  const rpm = Math.round((g.rpm[0] + (g.rpm[1] - g.rpm[0]) * Math.max(0, st.rev)) / 10) * 10;
  const load = st.load < -0.2 ? 'on the wheels' : st.load < 0.1 ? 'light' : st.load < 0.5 ? 'pulling' : st.load < 0.85 ? 'working' : 'flat out';
  return { gear, rpm, load };
}
